<template>
  <div class="home">
    <atomic-hidden-title
      :current-locale-data="currentLocaleData"
      :default-locale-data="defaultLocaleData"
      content-path="IPageMeta.title"
    />

    <client-only>
      <main-slider
        v-if="filteredSlider.length && isLoggedIn"
        :slides="filteredSlider"
      />

      <card-home
        v-if="!isLoggedIn && getContent(currentLocaleData, defaultLocaleData, 'banner')"
        v-bind="getContent(currentLocaleData, defaultLocaleData, 'banner')"
      />
    </client-only>

    <div class="home-main">
      <card-category :categories="getContent(currentLocaleData, defaultLocaleData, 'categories')" />

      <group-recently v-if="isLoggedIn" />

      <group-winners is-mini />

      <group-sport-events-widget
        :current-locale-data="currentLocaleData"
        :default-locale-data="defaultLocaleData"
      />

      <group-sport-cards v-if="!loading" />

      <group-games-adaptive
        v-if="!collectionsLoaded || hotGamesCollection"
        key="hot"
        show-all-btn
        show-arrows
        :category="hotGamesCollection"
      />

      <!--      Ждем Бэк -->
      <group-recommended-games v-if="isLoggedIn && false" />

      <client-only>
        <list-pay-methods v-if="!isTelegramUser" />
      </client-only>

      <group-games-adaptive
        key="all"
        show-all-btn
        show-arrows
        :category="allGameCollection"
      />

      <group-providers
        show-arrows
        show-all-btn
      />

      <group-promotions />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IHomePage } from '~/types';
import type { ICollection } from '@skeleton/core/types';

import { ModalName } from '@skeleton/consts/modals';

const route = useRoute();
const router = useRouter();
const dayjs = useDayjs();

const { openModalSync } = useModalStore();
const profileStore = useProfileStore();
const { getContent } = useProjectMethods();
const { setReferralId } = profileStore;
const { isTelegramUser, isLoggedIn } = storeToRefs(profileStore);

const { getContentData: getHomeContentData } = useContentLogic<IHomePage>({
  contentKey: 'homePageContent',
  contentRoute: ['pages', 'home'],
  isPage: true,
});

const { data: homeContent } = await useLazyAsyncData(getHomeContentData);

const currentLocaleData = computed(() => homeContent.value?.currentLocaleData);
const defaultLocaleData = computed(() => homeContent.value?.defaultLocaleData);

const { getContentData: getSliderContentData } = useContentLogic<IHomePage>({
  contentKey: 'casinoPageContent',
  contentRoute: ['pages', 'casino'],
  isPage: false,
});

const { data: sliderContent } = await useLazyAsyncData(getSliderContentData);

const { getCollectionsList, findGameCollectionByIdentity } = useGamesStore();
const { data: gameCollections, pending: pendingGames } = await useLazyAsyncData(() => getCollectionsList(), {
  server: false,
});

const filteredSlider = computed(() => {
  const sliders = getContent(
    sliderContent.value?.currentLocaleData,
    sliderContent.value?.defaultLocaleData,
    'slider',
    [],
  );
  if (!sliders.length) {
    return [];
  }

  const { filteredSlider } = useSliderFilter({
    slider: sliders,
    isLoggedIn: isLoggedIn.value,
    isTelegramUser: isTelegramUser.value,
    sliderFilterTime: dayjs.utc().toString(),
  });

  return filteredSlider.value;
});

const collectionsLoaded = computed(() => Array.isArray(gameCollections.value));

const hotGamesCollection = computed(() => getGameCollection('hot'));
const allGameCollection = computed(() => getGameCollection('All'));

const getGameCollection = (identity: string): ICollection | undefined => {
  return findGameCollectionByIdentity(gameCollections.value || [], identity);
};

const loadingStore = useLoadingStore();

const loading = computed(() => {
  return !homeContent.value || !sliderContent.value || pendingGames.value;
});

watchEffect(() => {
  loadingStore.setLoadingState(loading.value);
});

onMounted(async () => {
  if (route.query.start) {
    setReferralId(route.query.start.toString().replace('ref_id-', ''));
    const { query } = useRoute();
    delete query.start;
    await router.replace({ query });
    openModalSync(ModalName.SIGN_IN);
  }
});
</script>

<style src="~/assets/styles/pages/index.scss" lang="scss" />
